import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtDT, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, Loading, EmptyState } from "@/components/Shared";
import { toast } from "sonner";
import { Repeat, Plus, X, MapPin, Phone, MessageSquarePlus, Lock, Gift, Tag, Search, MessageCircle } from "lucide-react";

const CATEGORIES = ["Soins", "Solaire", "Maquillage", "Compléments alimentaires", "Capillaire", "Bébé & Maman", "Autre"];

export default function Exchanges() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPharmacist = user?.role === "pharmacist";
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("open");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [intFor, setIntFor] = useState(null);
  const [msg, setMsg] = useState("");

  const load = () => {
    if (tab === "mine") { api.get("/exchanges/mine").then((r) => setItems(r.data)).catch(() => setItems([])); return; }
    const params = { status: tab === "all" ? "closed" : "open" };
    if (cat) params.category = cat;
    if (q) params.q = q;
    api.get("/exchanges", { params }).then((r) => setItems(r.data)).catch(() => setItems([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, cat]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.product_name) { toast.error("Indiquez le produit"); return; }
    try {
      await api.post("/exchanges", {
        ...form,
        quantity: form.quantity ? parseInt(form.quantity, 10) : null,
        price: form.price ? parseFloat(form.price) : null,
      });
      toast.success("Annonce publiée au réseau");
      setOpen(false); setForm({}); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendInterest = async () => {
    try {
      await api.post(`/exchanges/${intFor}/interest`, { message: msg });
      const t = await api.post(`/messages/threads`, { exchange_id: intFor });
      toast.success("Le pharmacien a été notifié. Discussion ouverte.");
      setIntFor(null); setMsg(""); load();
      navigate(`/messages/${t.data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openChat = async (eid) => {
    try {
      const r = await api.post(`/messages/threads`, { exchange_id: eid });
      navigate(`/messages/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const close = async (id) => {
    try { await api.post(`/exchanges/${id}/close`); toast.success("Annonce clôturée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (items === null) return <Loading />;
  const input = "mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-shadow text-sm";
  const tabs = isPharmacist ? [["open", "Annonces ouvertes"], ["mine", "Mes annonces"], ["all", "Clôturées"]] : [["open", "Annonces ouvertes"], ["all", "Clôturées"]];

  return (
    <div>
      <PageHeader title="Échanges entre pharmaciens" subtitle="Proposez vos surstocks de parapharmacie et contactez le réseau.">
        {isPharmacist && (
          <button onClick={() => setOpen(true)} data-testid="new-exchange" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors duration-200"><Plus className="h-4 w-4" /> Proposer un surstock</button>
        )}
      </PageHeader>

      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="flex gap-2 flex-wrap">
          {tabs.map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`ex-tab-${v}`}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 ${tab === v ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        {tab !== "mine" && (
          <div className="flex gap-2 md:ml-auto">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="ex-search" placeholder="Rechercher..." className="h-9 pl-8 pr-3 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600" />
            </div>
            <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="ex-cat" className="h-9 px-3 rounded-lg border border-slate-300 text-sm bg-white font-medium">
              <option value="">Toutes catégories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <EmptyState title="Aucune annonce" subtitle={isPharmacist ? "Proposez un surstock pour dépanner un confrère." : "Aucun surstock proposé actuellement."} icon={Repeat} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((e) => {
            const mine = isPharmacist && e.pharmacist_id === user.id;
            const free = !e.price;
            return (
              <div key={e.id} className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col" data-testid={`exchange-${e.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <span className={`h-10 w-10 rounded-lg grid place-items-center shrink-0 ${free ? "bg-emerald-50 text-emerald-600" : "bg-indigo-50 text-indigo-600"}`}>{free ? <Gift className="h-5 w-5" /> : <Tag className="h-5 w-5" />}</span>
                  {e.category && <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">{e.category}</span>}
                </div>
                <div className="mt-3 font-bold text-slate-900 leading-snug">{e.product_name}</div>
                <div className="text-xs text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
                  {e.quantity ? <span className="num">Qté {e.quantity}</span> : null}
                  {e.brand && <span>· {e.brand}</span>}
                  {e.expiry && <span>· Exp. {e.expiry}</span>}
                </div>
                {e.note && <p className="mt-2 text-sm text-slate-600 flex-1">{e.note}</p>}
                <div className="mt-3 flex items-center justify-between">
                  <span className={`text-sm font-bold ${free ? "text-emerald-600" : "text-slate-900"} num`}>{free ? "Échange / Gratuit" : fmtDT(e.price)}</span>
                  {e.status === "closed" && <StatusBadge status="closed" />}
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                  <span className="text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{e.pharmacist_name}{e.city ? ` · ${e.city}` : ""}</span>
                </div>
                {e.status === "open" && (
                  <div className="mt-3 flex gap-2">
                    {mine ? (
                      <>
                        <span className="text-xs font-semibold text-slate-500 num self-center flex-1">{e.interests?.length || 0} intéressé(s)</span>
                        <button onClick={() => close(e.id)} data-testid={`close-exchange-${e.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 text-xs font-semibold transition-colors duration-200"><Lock className="h-3.5 w-3.5" /> Clôturer</button>
                      </>
                    ) : isPharmacist ? (
                      <>
                        <button onClick={() => setIntFor(e.id)} data-testid={`interest-${e.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors duration-200"><MessageSquarePlus className="h-4 w-4" /> Je suis intéressé</button>
                        <button onClick={() => openChat(e.id)} data-testid={`chat-${e.id}`} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold transition-colors duration-200" title="Discuter avec le pharmacien"><MessageCircle className="h-4 w-4" /></button>
                      </>
                    ) : null}
                  </div>
                )}
                {/* Interests visible to owner */}
                {mine && e.interests?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                    {e.interests.map((it, i) => (
                      <div key={i} className="text-xs flex items-start justify-between gap-2" data-testid={`interest-item-${e.id}-${i}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-700">{it.pharmacist_name}{it.city ? ` · ${it.city}` : ""}</div>
                          {it.message && <div className="text-slate-500">{it.message}</div>}
                          {it.phone && <a href={`tel:${it.phone}`} className="inline-flex items-center gap-1 text-indigo-600 font-semibold mt-0.5"><Phone className="h-3 w-3" />{it.phone}</a>}
                        </div>
                        <button onClick={async () => { try { const r = await api.post(`/messages/threads`, { exchange_id: e.id, other_user_id: it.pharmacist_id }); navigate(`/messages/${r.data.id}`); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } }} data-testid={`chat-interest-${e.id}-${i}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-semibold transition-colors duration-200"><MessageCircle className="h-3 w-3" /> Répondre</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New exchange modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(ev) => ev.stopPropagation()} data-testid="exchange-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-lg text-slate-900">Proposer un surstock</h3><button onClick={() => setOpen(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <div className="space-y-3">
              <div><label className="text-sm font-semibold text-slate-700">Produit</label><input data-testid="ex-product" onChange={set("product_name")} className={input} placeholder="Nom du produit" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-semibold text-slate-700">Catégorie</label>
                  <select onChange={set("category")} className={input} data-testid="ex-category"><option value="">—</option>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                <div><label className="text-sm font-semibold text-slate-700">Marque</label><input onChange={set("brand")} className={input} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-sm font-semibold text-slate-700">Quantité</label><input type="number" onChange={set("quantity")} className={input} /></div>
                <div><label className="text-sm font-semibold text-slate-700">Péremption</label><input onChange={set("expiry")} className={input} placeholder="MM/AAAA" /></div>
                <div><label className="text-sm font-semibold text-slate-700">Prix (DT)</label><input type="number" onChange={set("price")} className={input} placeholder="0 = échange" /></div>
              </div>
              <div><label className="text-sm font-semibold text-slate-700">Note</label><textarea onChange={set("note")} className={input + " h-16 py-2"} placeholder="Précisions (état, motif du surstock...)" /></div>
            </div>
            <button onClick={submit} data-testid="submit-exchange" className="mt-5 w-full h-11 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-colors duration-200">Publier au réseau</button>
          </div>
        </div>
      )}

      {/* Interest modal */}
      {intFor && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setIntFor(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(ev) => ev.stopPropagation()} data-testid="interest-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-lg text-slate-900">Manifester votre intérêt</h3><button onClick={() => setIntFor(null)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <p className="text-sm text-slate-500 mb-3">Vos coordonnées (téléphone, ville) seront partagées avec le pharmacien pour vous recontacter.</p>
            <textarea data-testid="interest-message" value={msg} onChange={(e) => setMsg(e.target.value)} className={input + " h-24 py-2"} placeholder="Bonjour, je suis intéressé par ce produit..." />
            <button onClick={sendInterest} data-testid="submit-interest" className="mt-4 w-full h-11 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-colors duration-200">Envoyer</button>
          </div>
        </div>
      )}
    </div>
  );
}
